// CB:BEGIN notification-preferences-panel
import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';

interface PreferenceToggle {
  key: string;
  label: string;
  required?: boolean;
}

const CONTRACTOR_PREFS: PreferenceToggle[] = [
  { key: 'NEW_PROJECT_AVAILABLE', label: 'New bid opportunities in my service area' },
  { key: 'BID_ACCEPTED', label: 'Updates on projects I\'ve bid on (bid accepted/rejected)', required: true },
  { key: 'BID_REJECTED', label: 'Bid outcome notifications' },
  { key: 'MILESTONE_APPROVED', label: 'Milestone approval notifications' },
  { key: 'PAYMENT_RELEASED', label: 'Payment notifications', required: true },
  { key: 'ADMIN_DIRECT_MESSAGE', label: 'Important account/admin notices', required: true },
];

const HOMEOWNER_PREFS: PreferenceToggle[] = [
  { key: 'BID_RECEIVED', label: 'Notify me when I receive new bids' },
  { key: 'PROJECT_SUBMITTED', label: 'Notify me about project status updates' },
  { key: 'MILESTONE_SUBMITTED', label: 'Notify me when milestones are submitted' },
  { key: 'BID_ACCEPTED', label: 'Bid acceptance confirmations', required: true },
  { key: 'PAYMENT_RELEASED', label: 'Payment notifications', required: true },
  { key: 'ADMIN_DIRECT_MESSAGE', label: 'Important account/admin notices', required: true },
];

interface NotificationPreferencesPanelProps {
  role: 'CONTRACTOR' | 'HOMEOWNER';
}

const NotificationPreferencesPanel: React.FC<NotificationPreferencesPanelProps> = ({ role }) => {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [perEvent, setPerEvent] = useState<Record<string, { email: boolean }>>({});
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(false);

  const prefs = role === 'CONTRACTOR' ? CONTRACTOR_PREFS : HOMEOWNER_PREFS;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const res = await fetch(getApiUrl('/api/notifications/preferences'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setEmailEnabled(data.emailEnabled !== false);
          setPerEvent(data.perEvent || {});
        }
      } catch (e) {
        console.error('Failed to load notification preferences', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, getToken]);

  const isEventEnabled = (key: string): boolean => {
    if (!emailEnabled) return false;
    const override = perEvent[key];
    return override?.email !== false;
  };

  const toggleEvent = (key: string, required: boolean) => {
    if (required) return;
    setPerEvent(prev => {
      const current = prev[key]?.email !== false;
      return { ...prev, [key]: { email: !current } };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = await getToken();
      await fetch(getApiUrl('/api/notifications/preferences'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ emailEnabled, perEvent }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('Failed to save notification preferences', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 24, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 600,
          color: '#111827',
          borderRadius: 8,
        }}
      >
        <span>Notification Preferences</span>
        <span style={{ fontSize: 12, color: '#6b7280' }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {loading ? (
            <p style={{ color: '#6b7280', fontSize: 13 }}>Loading...</p>
          ) : (
            <>
              {/* Global email toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f3f4f6' }}>
                <input
                  id="np-email-global"
                  type="checkbox"
                  checked={emailEnabled}
                  onChange={e => setEmailEnabled(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                <label htmlFor="np-email-global" style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                  Enable email notifications
                </label>
              </div>

              {/* Per-event toggles */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, opacity: emailEnabled ? 1 : 0.5 }}>
                {prefs.map(pref => (
                  <div key={pref.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      id={`np-${pref.key}`}
                      type="checkbox"
                      checked={pref.required || isEventEnabled(pref.key)}
                      onChange={() => toggleEvent(pref.key, !!pref.required)}
                      disabled={!emailEnabled || pref.required}
                      style={{ width: 16, height: 16 }}
                    />
                    <label htmlFor={`np-${pref.key}`} style={{ fontSize: 13, color: '#374151' }}>
                      {pref.label}
                      {pref.required && (
                        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>(required)</span>
                      )}
                    </label>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-primary"
                  style={{ fontSize: 13, padding: '8px 16px' }}
                >
                  {saving ? 'Saving...' : 'Save Preferences'}
                </button>
                {saved && (
                  <span style={{ fontSize: 13, color: '#059669' }}>Preferences saved.</span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationPreferencesPanel;
// CB:END notification-preferences-panel
